import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import VehicleRegistrationService from "../services/vehicleRegistrationService";
import Breadcrumb from "../components/Breadcrumb";
import StorageService from "../services/StorageService";
import ReactPaginate from "react-paginate";
import Loading from "../components/loading";
import XLSX from "xlsx-js-style";
import usePermission from "../utils/UsePermission";
import ScreenData from "../config/ScreenData";
import MessageDialog from "../components/MessageDialog";


export default function ViewVehicleRegistration() {
  const [vehicles, setVehicles] = useState([]);
  const [selected, setSelected] = useState(null); //  RADIO (single select)
  const [searchText, setSearchText] = useState(""); //  SEARCH
  const [message, setMessage] = useState("");
  const [msgType, setMsgType] = useState("error");
  const [loading, setLoading] = useState(true);
  // const messageTimer = useRef(null);
  const [pageCount, setPageCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 20;

  const navigate = useNavigate();



  const fetchVehicles = async () => {
    setLoading(true);
    try {
      const activeCompany = StorageService.activeCompany();
      if (!activeCompany?.companyId) {
        showMessage("Company ID missing. Please login again.");
        return;
      }

      const search = searchText.trim().toLowerCase();
      const res = search
        ? await VehicleRegistrationService.exportVehicleRegistration(activeCompany.companyId)
        : await VehicleRegistrationService.getAll(
          currentPage,
          itemsPerPage,
          searchText
        );

      let data = res?.data || [];

      if (search) {
        data = data.filter(
          (v) =>
            v.vehicleName?.toLowerCase().includes(search) ||
            v.registrationNo?.toLowerCase().includes(search)
        );
      }

      data = data.map((v) => ({
        ...v,
        status: v.status || (v.isActive === 1 ? "Active" : "Inactive"),
      }));

      setVehicles(search ? data.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage) : data);
      setPageCount(search ? Math.ceil(data.length / itemsPerPage) : res.totalPages || 0);

    } catch (error) {
      console.error(error);
      showMessage("Error loading vehicle registrations.");
    } finally {
      setLoading(false);
    }
  };


  const exportExcel = async () => {
    if (!can("canView")) {
      showMessage("No permission", "error");
      return;
    }
    try {
      const company = StorageService.activeCompany();

      const res =
        await VehicleRegistrationService.exportVehicleRegistration(
          company.companyId
        );

      if (!res?.status || !res.data.length) {
        showMessage("No data found", "error");
        return;
      }

      const data = res.data;

      const wb = XLSX.utils.book_new();
      const ws = {};
      ws["!merges"] = [];

      let row = 0;

      // ✅ STYLES
      const thin = { style: "thin" };
      const thick = { style: "medium" };

      const center = {
        horizontal: "center",
        vertical: "center",
        wrapText: true,
      };

      // =========================
      // TITLE
      // =========================
      XLSX.utils.sheet_add_aoa(ws, [["Vehicle Registration Report"]], {
        origin: { r: row, c: 1 },
      });

      ws["!merges"].push({
        s: { r: row, c: 1 },
        e: { r: row, c: 10 },
      });

      const titleCell = XLSX.utils.encode_cell({ r: row, c: 1 });

      ws[titleCell].s = {
        font: { bold: true, sz: 16 },
        alignment: center,
      };

      row += 2;

      // =========================
      // HEADER
      // =========================
      const header = [
        "Company",
        "Vehicle Name",
        "Registration No",
        "Vehicle Type",
        "Reading Type",
        "Vendor Category",
        "Vendor",
        "Ownership",
        "Status",
        "Created Date",
      ];

      const startRow = row; // ✅ START TRACK

      XLSX.utils.sheet_add_aoa(ws, [header], {
        origin: { r: row, c: 1 },
      });

      header.forEach((_, i) => {
        const cell = XLSX.utils.encode_cell({ r: row, c: i + 1 });

        ws[cell].s = {
          font: { bold: true },
          alignment: center,
        };
      });

      row++;

      // =========================
      // DATA
      // =========================
      data.forEach((v) => {
        const rowData = [
          v.companyName,
          v.vehicleName,
          v.registrationNo,
          v.vehicleType,
          v.readingType,
          v.vendorCategoryName,
          v.vendorName,
          v.ownerShipType,
          v.status,
          v.createdDate
            ? new Date(v.createdDate).toLocaleDateString("en-GB")
            : "",
        ];

        XLSX.utils.sheet_add_aoa(ws, [rowData], {
          origin: { r: row, c: 1 },
        });

        row++;
      });

      const endRow = row - 1; // ✅ END TRACK

      // =========================
      // APPLY BORDERS 🔥
      // =========================
      const colCount = header.length;

      for (let r = startRow; r <= endRow; r++) {
        for (let c = 1; c <= colCount; c++) {
          const cell = XLSX.utils.encode_cell({ r, c });

          if (!ws[cell]) {
            ws[cell] = { t: "s", v: "" };
          }

          ws[cell].s = ws[cell].s || {};
          ws[cell].s.alignment = center;

          ws[cell].s.border = {
            top: r === startRow ? thick : thin,
            bottom: r === endRow ? thick : thin,
            left: c === 1 ? thick : thin,
            right: c === colCount ? thick : thin,
          };
        }
      }

      // =========================
      // COLUMN WIDTH
      // =========================
      ws["!cols"] = [
        { wch: 20 },
        { wch: 25 },
        { wch: 20 },
        { wch: 20 },
        { wch: 15 },
        { wch: 20 },
        { wch: 20 },
        { wch: 15 },
        { wch: 12 },
        { wch: 18 },
      ];

      // =========================
      // EXPORT
      // =========================
      XLSX.utils.book_append_sheet(wb, ws, "VehicleRegistration");

      XLSX.writeFile(wb, "VehicleRegistration.xlsx");
    } catch (error) {
      console.error(error);
      showMessage("Error exporting data", "error");
    }
  };

  const showMessage = (text, type = "error") => {
    setMessage(text);
    setMsgType(type);
    // if (messageTimer.current) clearTimeout(messageTimer.current);
    // messageTimer.current = setTimeout(() => setMessage(""), 4000);
  };

  const { permission, checkView, can } = usePermission(
    ScreenData.Vehicle_Registration,
    showMessage
  );

  useEffect(() => {
    if (permission) {
      if (checkView()) {
        fetchVehicles();
      }
    }
  }, [currentPage, permission, searchText]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchText]);

  // const closeMessage = () => {
  //   setMessage("");
  //   if (messageTimer.current) clearTimeout(messageTimer.current);
  // };

  //  RADIO SELECT
  const handleSelect = (id) => {
    setSelected(id);
  };

  const handlePageClick = (event) => {
    const selectedPage = event.selected + 1;
    setCurrentPage(selectedPage);
  };


  const handleAdd = () => {
    if (!can("canAdd")) {
      showMessage("No permission", "error");
      return;
    }
    navigate("/cpnl/Vehicles/add");
  };

  const handleModify = () => {
    if (!can("canModify")) {
      showMessage("No permission", "error");
      return;
    }
    if (!selected) {
      showMessage("Please select one vehicle to modify.");
      return;
    }
    navigate(`/cpnl/Vehicles/add/${selected}`);
  };

  const handleToggleStatus = async (vehicle) => {
    if (!can("canModify")) {
      showMessage("No permission", "error");
      return;
    }
    const newStatus = vehicle.status === "Active" ? "Inactive" : "Active";
    if (!window.confirm(`Are you sure you want to change this as ${newStatus}?`))
      return;

    setLoading(true);
    try {
      await VehicleRegistrationService.updateStatus(vehicle.id);

      showMessage(`Vehicle changed as ${newStatus}!`, "success");

      setVehicles((prev) =>
        prev.map((v) =>
          v.id === vehicle.id ? { ...v, status: newStatus } : v
        )
      );
    } catch (error) {
      console.error(error);
      showMessage("Error updating vehicle status.");
    } finally {
      setLoading(false);
    }
  };

  const filteredVehicles = vehicles;

  return (
    <>
      <Breadcrumb pageTitle="Vehicle Registrations" secondTitle="Masters" />

      <div className="row">
        <div className="card py-2 bg-white shadow-sm">

          {/* {message && (
            <div
              className={`alert ${msgType === "success" ? "alert-success" : "alert-danger"
                } d-flex justify-content-between align-items-center w-100 mb-2 p-2`}
            >
              <span>{message}</span>
              <button
                type="button"
                className="btn-close"
                onClick={closeMessage}
              ></button>
            </div>
          )} */}


          <MessageDialog
            message={message}
            type={msgType}
            setMessage={setMessage}
            setMsgType={setMsgType}
          />


          {/*  SEARCH + ADD / MODIFY */}
          <div className="mb-3 pb-2 border-bottom d-flex align-items-end gap-3">
            <div>
              <input
                type="text"
                className="form-control"
                placeholder="Search Vehicle"
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
              />
            </div>

            <div className="ms-auto d-flex gap-2">
              {can("canAdd") && (
                <button
                  className="d-inline-flex btn btn-success"
                  onClick={handleAdd}
                  disabled={loading}
                >
                  <i className="ti ti-plus me-1"></i> Add
                </button>
              )}

              {can("canModify") && (
                <button
                  className="d-inline-flex btn btn-warning"
                  onClick={handleModify}
                  disabled={loading}
                >
                  <i className="ti ti-pencil me-1"></i> Modify
                </button>
              )}

              {can("canView") && (
                <button
                  className="d-inline-flex btn btn-primary"
                  onClick={exportExcel}
                  disabled={loading}
                >
                  <i className="ti ti-download me-1"></i> Export Excel
                </button>
              )}
            </div>
          </div>

          {/*  TABLE */}
          <div className="table-responsive">
            <table className="table table-striped table-bordered mb-0">
              <thead className="table-light">
                <tr>
                  <th>Select</th>
                  <th>Vehicle Owner Name</th>
                  <th>Registration No</th>
                  <th>Vehicle Type</th>
                  <th>Reading Type</th>
                  <th>Vendor CategoryName</th>
                  <th>Vendor Name</th>
                  <th>Ownership</th>
                  <th>Status</th>
                </tr>
              </thead>

              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="5" className="text-center py-4">
                      <Loading />
                    </td>
                  </tr>
                ) : filteredVehicles.length > 0 ? (
                  filteredVehicles.map((v) => {
                    const isActive = v.status === "Active";
                    return (
                      <tr key={v.id}>
                        <td>
                          <input
                            type="radio"
                            name="vehicleSelect"
                            disabled={v.status === "Inactive"}
                            checked={selected === v.id}
                            onChange={() => handleSelect(v.id)}
                          />
                        </td>
                        <td>{v.vehicleName}</td>
                        <td>{v.registrationNo}</td>
                        <td>{v.vehicleType || v.vehicleTypeName || "-"}</td>
                        <td>{v.readingType}</td>
                        <td>{v.vendorCategoryName}</td>
                        <td>{v.vendorName}</td>
                        <td>{v.ownerShipType || "-"}</td>
                        <td>
                          <span
                            className={`badge ${isActive ? "bg-success" : "bg-secondary"
                              }`}
                            style={{ cursor: "pointer" }}
                            onClick={() => handleToggleStatus(v)}
                          >
                            {v.status}
                          </span>
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan="7" className="text-center text-muted">
                      No vehicles found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>

            <ReactPaginate
              breakLabel="..."
              nextLabel="Next >"
              onPageChange={handlePageClick}
              pageRangeDisplayed={3}
              marginPagesDisplayed={2}
              pageCount={pageCount}
              previousLabel="< Previous"
              containerClassName="pagination-container"
              pageClassName="page-item"
              pageLinkClassName="page-link"
              previousClassName="page-item"
              previousLinkClassName="page-link"
              nextClassName="page-item"
              nextLinkClassName="page-link"
              activeClassName="active"
            />



          </div>

        </div>
      </div>
    </>
  );
}
